(() => {
  const controllers = new WeakMap();
  let dialogSequence = 0;

  function readJSON(element, fallback) {
    if (!element) return fallback;
    try { return JSON.parse(element.textContent); } catch { return fallback; }
  }

  function initialize(root) {
    if (controllers.has(root)) {
      controllers.get(root).refresh();
      return;
    }

    const strings = readJSON(root.querySelector('[data-umg-strings]'), {});
    const storageKey = `umg-discovery-favourites:${window.location.hostname}`;
    const productsKey = `${storageKey}:products`;
    const state = { filter: new URLSearchParams(window.location.search).get('favourites') === '1' ? 'saved' : 'all', artist: 'all', favourites: [] };
    let savedProducts = {};
    const recoveredProducts = new Map();
    const recoveryRequests = new Map();
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (Array.isArray(saved)) state.favourites = Array.from(new Set(saved.filter(id => typeof id === 'string' && /^\d+$/.test(id)))).slice(0,100);
      const products = JSON.parse(localStorage.getItem(productsKey) || '{}');
      if (products && typeof products === 'object' && !Array.isArray(products)) {
        state.favourites.forEach(id => {
          if (validHandle(products[id])) savedProducts[id] = products[id];
        });
      }
    } catch {}

    let dialog = null;
    let returnFocus = null;

    function saveFavourites() {
      savedProducts = Object.fromEntries(state.favourites.filter(id => validHandle(savedProducts[id])).map(id => [id, savedProducts[id]]));
      try {
        localStorage.setItem(storageKey, JSON.stringify(state.favourites));
        localStorage.setItem(productsKey, JSON.stringify(savedProducts));
      } catch {}
    }

    function announce(message) {
      const status = root.querySelector('[data-umg-status]');
      if (status && message) status.textContent = message;
    }

    function cards() {
      const grid = root.querySelector('[data-umg-filter-grid]');
      return grid ? Array.from(grid.querySelectorAll('.umg-product[data-product-id]')) : [];
    }

    function validHandle(handle) {
      return typeof handle === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/i.test(handle);
    }

    function rememberProduct(card) {
      const product = readJSON(card.querySelector('[data-umg-product-json]'), null);
      const id = String(card.dataset.productId);
      if (state.favourites.includes(id) && validHandle(product?.handle)) savedProducts[id] = product.handle;
    }

    function cloneCard(card) {
      const copy = card.cloneNode(true);
      copy.dataset.umgCatalogueExtra = 'true';
      copy.hidden = true;
      copy.removeAttribute('id');
      copy.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
      return copy;
    }

    function recoveredCard(product) {
      const template = root.querySelector('.umg-product[data-product-id]:not([data-umg-catalogue-extra])');
      const currency = root.dataset.currency || window.Shopify?.currency?.active;
      if (!template || !validHandle(product.handle) || !Number.isFinite(product.price) || !/^[A-Z]{3}$/.test(currency || '')) return null;
      const card = cloneCard(template);
      const type = typeof product.type === 'string' ? product.type : '';
      const tags = Array.isArray(product.tags) ? product.tags.join('|').toLocaleLowerCase() : '';
      const format = `${type.toLocaleLowerCase()}|${tags}`;
      const title = typeof product.title === 'string' ? product.title : '';
      const titleParts = title.split(' - ');
      const artist = titleParts.length > 1 ? titleParts.shift() : '';
      const url = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(product.handle)}`;
      card.dataset.productId = String(product.id);
      card.dataset.artist = artist;
      card.dataset.format = /lp|vinyl/.test(format) ? 'vinyl' : /cd/.test(format) ? 'cd' : 'other';
      card.dataset.search = `${title} ${type} ${tags}`;
      card.querySelectorAll('.umg-product-open, .umg-product-title').forEach(link => { link.href = url; });
      const titleLink = card.querySelector('.umg-product-title');
      if (titleLink) titleLink.textContent = artist ? titleParts.join(' - ') : title;
      const artworkLink = card.querySelector('.umg-product-open');
      if (artworkLink) artworkLink.setAttribute('aria-label', title);
      const artistLabel = card.querySelector('.umg-product-artist');
      if (artistLabel) { artistLabel.textContent = artist; artistLabel.hidden = !artist; }
      else if (artist && titleLink) titleLink.before(createElement('div', 'umg-product-artist', artist));
      const edition = card.querySelector('.umg-product-edition');
      if (edition) { edition.textContent = type; edition.hidden = !type; }
      else if (type && titleLink) titleLink.after(createElement('div', 'umg-product-edition', type));
      const price = card.querySelector('.umg-product-price');
      const money = new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' });
      if (price) price.textContent = product.price_varies && Number.isFinite(product.price_min) && Number.isFinite(product.price_max)
        ? `${money.format(product.price_min / 100)} – ${money.format(product.price_max / 100)}` : money.format(product.price / 100);
      card.querySelectorAll('.umg-product-tag').forEach(badge => badge.remove());
      const badgeKind = product.available === false ? 'sold-out' : tags.includes('pre-order') ? 'preorder' : tags.includes('store exclusive') || tags.includes('d2c') ? 'exclusive' : 'format';
      const badgeLabel = badgeKind === 'sold-out' ? card.dataset.badgeSoldOut : badgeKind === 'preorder' ? card.dataset.badgePreorder : badgeKind === 'exclusive' ? card.dataset.badgeExclusive : type;
      if (badgeLabel) {
        const badge = createElement('span', `umg-product-tag${badgeKind === 'sold-out' ? ' umg-product-tag--sold-out' : ''}`, badgeLabel);
        badge.dataset.umgBadge = badgeKind;
        card.querySelector('.umg-product-image')?.append(badge);
      }
      let image = card.querySelector('.umg-product-image img');
      const imageSource = product.featured_image || product.images?.[0];
      if (typeof imageSource === 'string' && artworkLink) {
        const imageURL = new URL(imageSource, window.location.origin);
        if (imageURL.protocol !== 'https:' && imageURL.protocol !== 'http:') return null;
        if (!image) { image = createElement('img'); image.loading = 'lazy'; artworkLink.append(image); }
        image.src = imageURL.href;
        image.removeAttribute('srcset');
        image.removeAttribute('width');
        image.removeAttribute('height');
        image.alt = title;
      } else if (image) image.remove();
      card.querySelector('[data-umg-favourite]')?.setAttribute('data-umg-favourite', String(product.id));
      const json = card.querySelector('[data-umg-product-json]');
      if (json) { json.dataset.umgProductJson = String(product.id); json.textContent = JSON.stringify(product); }
      return card;
    }

    function rebuildCatalogue() {
      const grid = root.querySelector('[data-umg-filter-grid]');
      if (!grid) return;
      grid.querySelectorAll('[data-umg-catalogue-extra]').forEach(card => card.remove());
      const ids = new Set(cards().map(card => String(card.dataset.productId)));
      root.querySelectorAll('.umg-product[data-product-id]').forEach(card => {
        rememberProduct(card);
        const id = String(card.dataset.productId);
        if (ids.has(id)) return;
        ids.add(id);
        grid.append(cloneCard(card));
      });
      recoveredProducts.forEach((product, id) => {
        if (!state.favourites.includes(id) || ids.has(id)) return;
        const card = recoveredCard(product);
        if (card) { grid.append(card); ids.add(id); }
      });
      saveFavourites();
    }

    function recoverFavourites() {
      if (state.filter !== 'saved') return;
      const grid = root.querySelector('[data-umg-filter-grid]');
      if (!grid) return;
      const ids = new Set(cards().map(card => String(card.dataset.productId)));
      state.favourites.forEach(id => {
        if (ids.has(id) || recoveryRequests.has(id) || !validHandle(savedProducts[id])) return;
        const request = { pending: true };
        recoveryRequests.set(id, request);
        fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(savedProducts[id])}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then(response => response.ok ? response.json() : null)
          .then(product => {
            if (!product || String(product.id) !== id || !state.favourites.includes(id) || cards().some(card => String(card.dataset.productId) === id)) return;
            const card = recoveredCard(product);
            if (!card) return;
            recoveredProducts.set(id, product);
            grid.append(card);
            rememberProduct(card);
            saveFavourites();
            renderArtistOptions();
            renderFavourites();
          })
          .catch(() => {})
          .finally(() => { request.pending = false; renderFilter(); });
      });
    }

    function renderFavourites() {
      root.querySelectorAll('[data-umg-favourite]').forEach(button => {
        const saved = state.favourites.includes(String(button.dataset.umgFavourite));
        button.classList.toggle('is-saved', saved);
        button.setAttribute('aria-pressed', String(saved));
        const title = button.closest('.umg-product')?.querySelector('.umg-product-title')?.textContent.trim() || '';
        const label = saved ? strings.remove_saved : strings.save_product;
        if (label) button.setAttribute('aria-label', label.replace('__TITLE__', title));
      });
    }

    function renderFilter() {
      recoverFavourites();
      const grid = root.querySelector('[data-umg-filter-grid]');
      const initialLimit = Math.max(1, Number.parseInt(grid?.dataset.initialLimit || '4', 10) || 4);
      const defaultFeatured = state.filter === 'all' && state.artist === 'all';
      let count = 0;
      cards().forEach((card, index) => {
        const id = String(card.dataset.productId);
        const format = (card.dataset.format || '').toLocaleLowerCase();
        const artist = card.dataset.artist || '';
        const matchesFormat = state.filter === 'all' || (state.filter === 'saved' ? state.favourites.includes(id) : format === state.filter);
        const visible = (!defaultFeatured || index < initialLimit) && matchesFormat && (state.artist === 'all' || artist === state.artist);
        card.hidden = !visible;
        if (visible) count += 1;
      });
      root.querySelectorAll('[data-umg-filter]').forEach(button => {
        const active = button.dataset.umgFilter === state.filter;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      const results = root.querySelector('[data-umg-results]');
      const countText = count === 1 ? strings.results_one : strings.results_many;
      const pending = state.filter === 'saved' && Array.from(recoveryRequests.values()).some(request => request.pending);
      if (grid) grid.setAttribute('aria-busy', String(pending));
      if (results && countText) results.textContent = pending ? '' : countText.replace('__COUNT__', String(count));
      const empty = root.querySelector('[data-umg-empty]');
      if (empty) empty.hidden = count > 0 || pending;
      const artistSelect = root.querySelector('[data-umg-artist-filter]');
      if (artistSelect) artistSelect.value = state.artist;
    }

    function renderArtistOptions() {
      const select = root.querySelector('[data-umg-artist-filter]');
      if (!select) return;
      const artists = Array.from(new Set(cards().map(card => card.dataset.artist).filter(Boolean))).sort((a,b) => a.localeCompare(b));
      Array.from(select.options).forEach(option => { if (option.value !== 'all') option.remove(); });
      artists.forEach(artist => {
        const option = document.createElement('option');
        option.value = artist;
        option.textContent = artist;
        select.append(option);
      });
      if (state.artist !== 'all' && !artists.includes(state.artist)) state.artist = 'all';
    }

    function createElement(tag, className, text) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text) element.textContent = text;
      return element;
    }

    function quickView(trigger) {
      const card = trigger.closest('.umg-product');
      if (!card) return;
      const product = readJSON(card.querySelector('[data-umg-product-json]'), null);
      const productLink = card.querySelector('.umg-product-open[href]') || card.querySelector('.umg-product-title[href]');
      if (!productLink) return;
      if (!product || typeof HTMLDialogElement === 'undefined' || !strings.close || !strings.view_product) {
        window.location.assign(productLink.href);
        return;
      }

      if (!dialog) {
        dialog = createElement('dialog', 'umg-dialog');
        root.append(dialog);
        dialog.addEventListener('click', event => {
          if (event.target === dialog) dialog.close();
        });
        dialog.addEventListener('close', () => {
          if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
        });
      }
      returnFocus = trigger;
      dialog.replaceChildren();
      const header = createElement('div', 'umg-dialog-header');
      header.append(createElement('span', '', strings.quick_view || trigger.textContent.trim()));
      const close = createElement('button', 'umg-close');
      close.type = 'button';
      close.setAttribute('aria-label', strings.close);
      const glyph = createElement('span', 'umg-close-glyph', '×');
      glyph.setAttribute('aria-hidden', 'true');
      close.append(glyph);
      close.addEventListener('click', () => dialog.close());
      header.append(close);

      const content = createElement('div', 'umg-dialog-content');
      const cardImage = card.querySelector('.umg-product-image img');
      if (cardImage) {
        const image = createElement('img', 'umg-dialog-art');
        image.src = cardImage.currentSrc || cardImage.src;
        image.alt = cardImage.alt;
        content.append(image);
      }
      const details = createElement('div', 'umg-dialog-details');
      const artist = card.querySelector('.umg-product-artist')?.textContent.trim() || card.dataset.artist || '';
      const title = card.querySelector('.umg-product-title')?.textContent.trim() || product.title || '';
      const edition = card.querySelector('.umg-product-edition')?.textContent.trim() || product.type || '';
      const price = card.querySelector('.umg-product-price')?.textContent.trim() || '';
      if (artist) details.append(createElement('p', 'umg-detail-artist', artist));
      const heading = createElement('h2', '', title);
      heading.id = `umg-product-dialog-title-${++dialogSequence}`;
      dialog.setAttribute('aria-labelledby', heading.id);
      details.append(heading);
      if (edition) details.append(createElement('p', 'umg-detail-edition', edition));
      if (price) details.append(createElement('p', 'umg-detail-price', price));
      if (strings.availability_details) details.append(createElement('p', 'umg-detail-availability', strings.availability_details));
      const link = createElement('a', 'umg-primary', strings.view_product);
      link.href = productLink.href;
      details.append(link);
      content.append(details);
      dialog.append(header, content);
      if (!dialog.open) dialog.showModal();
      close.focus({ preventScroll: true });
    }

    root.addEventListener('click', event => {
      const target = event.target.closest('button');
      if (!target || !root.contains(target)) return;
      if (target.hasAttribute('data-umg-quick-view')) {
        quickView(target);
        return;
      }
      if (target.hasAttribute('data-umg-favourite')) {
        const id = String(target.dataset.umgFavourite);
        if (!/^\d+$/.test(id)) return;
        const saved = state.favourites.includes(id);
        state.favourites = saved ? state.favourites.filter(value => value !== id) : [...state.favourites, id].slice(0,100);
        const card = target.closest('.umg-product');
        if (card) rememberProduct(card);
        saveFavourites();
        renderFavourites();
        renderFilter();
        announce(saved ? strings.removed : strings.saved);
        return;
      }
      if (target.hasAttribute('data-umg-filter') || target.hasAttribute('data-umg-reset')) {
        state.filter = target.dataset.umgFilter || 'all';
        state.artist = 'all';
        renderFilter();
        if (!target.closest('[data-umg-catalogue]')) root.querySelector('[data-umg-catalogue]')?.scrollIntoView({ block: 'start', behavior: 'auto' });
      }
    });

    root.addEventListener('change', event => {
      if (!event.target.matches('[data-umg-artist-filter]')) return;
      state.artist = event.target.value;
      renderFilter();
    });

    root.addEventListener('submit', event => {
      if (!event.target.matches('[data-umg-newsletter-preview]')) return;
      event.preventDefault();
      const form = event.target;
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }
      const status = form.querySelector('[data-signup-status]');
      const message = form.dataset.successMessage || strings.newsletter_success;
      if (status && message) status.textContent = message;
      const email = form.querySelector('input[type="email"]');
      if (email) email.value = '';
    });

    const refresh = () => { rebuildCatalogue(); renderArtistOptions(); renderFilter(); renderFavourites(); };
    controllers.set(root, { refresh });
    refresh();
  }

  function initializeAll() {
    const root = document.querySelector('[data-umg-discovery-root]') || document.querySelector('body.umg-discovery');
    if (root) initialize(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
})();
